import { api } from './apiClient';
import type { ReferralSummary } from './billingApi';

export type Feature = 'media' | 'sports' | 'lobbies' | 'downloads' | 'livetv';
export type BillingType = 'monthly' | 'one_time';

export interface AdConfig {
  adsPerStream?: number;
  freeStreamsPerDay?: number;
}

export interface FeatureInfo {
  key: Feature;
  label: string;
  description: string;
}

/**
 * Static fallback catalog for the plan/trial feature selector. The set of
 * features is fixed (backend `ALL_FEATURES`, locked by billing.pricing.test.ts),
 * so the editor must not depend on a live `/features` round-trip to let a
 * recruiter pick what a plan includes. `listFeatures()` refines these labels
 * when it succeeds; when it 404s/errors (e.g. the recruiter backend is flag-off
 * on this environment) the selector still renders from this list. Order + copy
 * mirror the backend `FEATURE_INFO`.
 */
export const FEATURE_CATALOG: readonly FeatureInfo[] = [
  { key: 'media', label: 'TV, Movies & Anime', description: 'Full on-demand catalog: TV shows, movies, and anime.' },
  { key: 'sports', label: 'Sports', description: 'Sports VODs and live event streams.' },
  { key: 'lobbies', label: 'Watch Together', description: 'Shared lobbies to watch in sync with friends.' },
  { key: 'downloads', label: 'Downloads', description: 'Save streams as MP4 files to watch offline.' },
  { key: 'livetv', label: 'Live TV', description: 'Always-on live TV channels (news, entertainment, sports networks).' },
];

export interface RecruiterPlan {
  id: string;
  name: string;
  description: string | null;
  priceCents: number;
  billingType: BillingType;
  features: Feature[];
  adConfig: AdConfig | null;
  screenLimit: number;
  upgradePlanId: string | null;
  stripePriceId: string | null;
  archived: boolean;
  createdAt: string;
}

export interface RecruiterTrial {
  id: string;
  name: string;
  days: number;
  features: Feature[];
  adConfig: AdConfig | null;
  archived: boolean;
  createdAt: string;
}

export interface InviteToken {
  id: string;
  code: string;
  label: string | null;
  trialId: string;
  trialName: string | null;
  allowedPlanIds: string[] | null;
  createdAt: string;
  redeemed: boolean;
  redeemer: { displayName: string | null; email: string } | null;
}

export interface RecruitedUser {
  id: string;
  displayName: string | null;
  email: string;
  joinedAt: string;
  lastSeen: string | null;
  plans: { holdingId: string; planId: string; planName: string | null; kind: string; status: string }[];
  trial: { trialName: string | null; expiresAt: string; active: boolean } | null;
}

export interface PlanInput {
  name: string;
  description?: string | null;
  priceCents: number;
  billingType: BillingType;
  features: Feature[];
  adConfig?: AdConfig | null;
  screenLimit: number;
  upgradePlanId?: string | null;
}

export interface TrialInput {
  name: string;
  days: number;
  features: Feature[];
  adConfig?: AdConfig | null;
}

export const recruiterApi = {
  enabled(): Promise<{ enabled: boolean }> {
    return api<{ enabled: boolean }>('/api/recruiter/enabled', { auth: true });
  },
  listFeatures(): Promise<{ features: FeatureInfo[] }> {
    return api<{ features: FeatureInfo[] }>('/api/recruiter/features', { auth: true });
  },

  listPlans(includeArchived = false): Promise<{ plans: RecruiterPlan[] }> {
    return api<{ plans: RecruiterPlan[] }>(`/api/recruiter/plans${includeArchived ? '?includeArchived=true' : ''}`, { auth: true });
  },
  createPlan(input: PlanInput): Promise<{ plan: RecruiterPlan }> {
    return api<{ plan: RecruiterPlan }>('/api/recruiter/plans', { method: 'POST', body: input, auth: true });
  },
  updatePlan(id: string, input: PlanInput): Promise<{ plan: RecruiterPlan }> {
    return api<{ plan: RecruiterPlan }>(`/api/recruiter/plans/${encodeURIComponent(id)}`, { method: 'PUT', body: input, auth: true });
  },
  archivePlan(id: string): Promise<null> {
    return api<null>(`/api/recruiter/plans/${encodeURIComponent(id)}/archive`, { method: 'POST', auth: true });
  },

  listTrials(includeArchived = false): Promise<{ trials: RecruiterTrial[] }> {
    return api<{ trials: RecruiterTrial[] }>(`/api/recruiter/trials${includeArchived ? '?includeArchived=true' : ''}`, { auth: true });
  },
  createTrial(input: TrialInput): Promise<{ trial: RecruiterTrial }> {
    return api<{ trial: RecruiterTrial }>('/api/recruiter/trials', { method: 'POST', body: input, auth: true });
  },
  updateTrial(id: string, input: TrialInput): Promise<{ trial: RecruiterTrial }> {
    return api<{ trial: RecruiterTrial }>(`/api/recruiter/trials/${encodeURIComponent(id)}`, { method: 'PUT', body: input, auth: true });
  },
  archiveTrial(id: string): Promise<null> {
    return api<null>(`/api/recruiter/trials/${encodeURIComponent(id)}/archive`, { method: 'POST', auth: true });
  },

  mintToken(input: { trialId: string; allowedPlanIds?: string[]; label?: string }): Promise<{ token: InviteToken }> {
    return api<{ token: InviteToken }>('/api/recruiter/tokens', { method: 'POST', body: input, auth: true });
  },
  listTokens(): Promise<{ tokens: InviteToken[] }> {
    return api<{ tokens: InviteToken[] }>('/api/recruiter/tokens', { auth: true });
  },
  revokeToken(id: string): Promise<null> {
    return api<null>(`/api/recruiter/tokens/${encodeURIComponent(id)}`, { method: 'DELETE', auth: true });
  },

  listUsers(): Promise<{ users: RecruitedUser[] }> {
    return api<{ users: RecruitedUser[] }>('/api/recruiter/users', { auth: true });
  },
  cancelPlan(userId: string, holdingId: string): Promise<null> {
    return api<null>(`/api/recruiter/users/${encodeURIComponent(userId)}/cancel-plan`, { method: 'POST', body: { holdingId }, auth: true });
  },
  changePlan(userId: string, input: { planId: string; holdingId?: string }): Promise<unknown> {
    return api<unknown>(`/api/recruiter/users/${encodeURIComponent(userId)}/change-plan`, { method: 'POST', body: input, auth: true });
  },
  removeUser(userId: string): Promise<null> {
    return api<null>(`/api/recruiter/users/${encodeURIComponent(userId)}`, { method: 'DELETE', auth: true });
  },

  getEarnings(): Promise<ReferralSummary> {
    return api<ReferralSummary>('/api/recruiter/earnings', { auth: true });
  },
};
